import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DocumentFileByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetDocumentFileByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';
import { useTRPC } from '@/utils/trpc';

/**
 * Maps TRPC document file data to match the GraphQL query structure
 */
function mapTrpcDocumentFileToGraphQLQuery(
  trpcData: DocumentFileByIdResponseRow[]
): GetDocumentFileByIdQuery {
  return {
    document_file: trpcData,
  };
}

export const useGetDocumentFileById = (id: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.documentFile.documentFileById.queryOptions({ id }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcDocumentFileToGraphQLQuery(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () =>
      mapTRPCRefetch(trpcRefetch, mapTrpcDocumentFileToGraphQLQuery),
    error: trpcError,
  };
};
